import React, { ReactNode, useCallback, useContext, useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useNavigate } from 'react-router-dom';
import OfficeHoursService from '../../../../../../../services/OfficeHoursService';
import {
  IAvailableInviteSlotsParams,
  ISlot,
} from '../../../../../../../interfaces/officeHours';
import { MytaverseLogger } from '../../../../../../../helpers/logger';
import {
  getErrorNotification,
  NOTIFICATION_TYPES,
  useNotification,
} from '../../../../../../../components/Notification';

import { IOfficeHoursContext } from './interfaces';
import { getNotification } from '../../../../../../../components/Notification/helpers';
import { useMytaverseEvent } from '../../../../../../../modules/dashboard/providers';
import ROUTES from '../../../../../../../constants/routes';
import {
  getTimeFormat,
  convertToDate,
} from '../../../../../../../helpers/datetime';
import { useTotalSlots, useMytaverseFetch } from './hooks';

export const OfficeHoursContext = React.createContext<IOfficeHoursContext>(
  {} as IOfficeHoursContext,
);

export const useOfficeHours = () => useContext(OfficeHoursContext);

type MytaverseProviderProps = {
  children: ReactNode;
};

export const OfficeHoursProvider: React.FC<MytaverseProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { currentEvent } = useMytaverseEvent();

  const loadSlots = useCallback(
    async (params: IAvailableInviteSlotsParams, config: AxiosRequestConfig) => {
      const { slots, lastKey } =
        await OfficeHoursService.getAvailableInviteSlots(params, config);

      return {
        data: slots,
        lastKey,
      };
    },
    [],
  );

  const loadScheduledSlots = useCallback(
    async (params: IAvailableInviteSlotsParams, config: AxiosRequestConfig) => {
      const { slots, lastKey } =
        await OfficeHoursService.getAvailableInviteSlots(
          {
            ...params,
            isScheduled: true,
          },
          config,
        );

      return {
        data: slots,
        lastKey,
      };
    },
    [],
  );

  const {
    data: slots,
    loading: isSlotsLoading,
    callLoadData,
    setData: setSlots,
    resetState: resetSlots,
    loadNextPage: loadNextPageSlots,
  } = useMytaverseFetch(loadSlots);

  const {
    data: scheduledSlots,
    loading: isScheduledSlotsLoading,
    callLoadData: getScheduledSlots,
    setData: setScheduledSlots,
    resetState: resetScheduledSlots,
    loadNextPage: loadNextPageScheduledSlots,
  } = useMytaverseFetch(loadScheduledSlots);

  const {
    upcomingSlotsTotal,
    scheduledSlotsTotal,
    loadUpcomingSlots: loadUpcomingSlotsTotal,
    loadScheduledSlots: loadScheduledSlotsTotal,
    setScheduledSlotsTotal,
    setUpcomingSlotsTotal,
  } = useTotalSlots();

  const resetState = useCallback(() => {
    resetSlots();
    resetScheduledSlots();
    setScheduledSlotsTotal(0);
    setUpcomingSlotsTotal(0);
  }, [
    resetSlots,
    resetScheduledSlots,
    setScheduledSlotsTotal,
    setUpcomingSlotsTotal,
  ]);

  const loadSlotsData = useCallback(async () => {
    if (
      !currentEvent ||
      !currentEvent?.eventId ||
      !currentEvent?.inviteSlotsTags ||
      currentEvent?.inviteSlotsTags?.length === 0
    ) {
      return;
    }

    await Promise.all([
      callLoadData({ eventId: currentEvent?.eventId }),
      getScheduledSlots({ eventId: currentEvent?.eventId }),
      loadUpcomingSlotsTotal(currentEvent?.eventId),
      loadScheduledSlotsTotal(currentEvent?.eventId),
    ]);

    return;
  }, [currentEvent, callLoadData, getScheduledSlots, loadUpcomingSlotsTotal]);

  const handleLoadNextPageScheduledSlots = useCallback(() => {
    if (!currentEvent || !currentEvent?.eventId) {
      return;
    }

    loadNextPageScheduledSlots({ eventId: currentEvent.eventId });
  }, [currentEvent, loadNextPageScheduledSlots]);

  const handleLoadNextPageSlots = useCallback(() => {
    if (!currentEvent || !currentEvent?.eventId) {
      return;
    }

    loadNextPageSlots({ eventId: currentEvent.eventId });
  }, [currentEvent, loadNextPageSlots]);

  const handleBook = useCallback(
    async (eventId: string) => {
      try {
        const { slot } = await OfficeHoursService.bookSlot(eventId);

        if (slot) {
          if (currentEvent?.eventId) {
            getScheduledSlots({ eventId: currentEvent.eventId });
            setScheduledSlotsTotal((prevState) => prevState + 1);
          }

          setSlots((prevState: ISlot[]) =>
            prevState.map((item) => (item.eventId === eventId ? slot : item)),
          );
        }

        showNotification(
          getNotification({
            message: 'Your session was successfully booked',
            type: NOTIFICATION_TYPES.SUCCESS,
            withDismissButton: false,
          }),
        );
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const { message, response } = error;
          MytaverseLogger.error(
            JSON.stringify({ message, data: response?.data }),
          );

          showNotification(
            getErrorNotification({
              message: 'Your session was not booked',
              closeManually: true,
              withDismissButton: false,
            }),
          );
        }
      }
    },
    [getScheduledSlots, setSlots, currentEvent?.eventId],
  );

  const handleCancel = useCallback(
    async (eventId: string) => {
      try {
        const { slot } = await OfficeHoursService.cancelSlot(eventId);

        if (slot) {
          if (scheduledSlotsTotal > scheduledSlots.length) {
            getScheduledSlots({ eventId: currentEvent?.eventId as string });
          } else {
            setScheduledSlots((prevState: ISlot[]) =>
              prevState.filter((item) => item.eventId !== eventId),
            );
          }

          setSlots((prevState: ISlot[]) =>
            prevState.map((item) => (item.eventId === eventId ? slot : item)),
          );
          setScheduledSlotsTotal((prevState) => prevState - 1);
        }

        const name = `${slot.name} at ${getTimeFormat(
          convertToDate(slot.startTime),
        )}`;

        showNotification(
          getNotification({
            message: `Your session ${name} was successfully cancelled`,
            type: NOTIFICATION_TYPES.SUCCESS,
            withDismissButton: false,
          }),
        );
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const { message, response } = error;
          MytaverseLogger.error(
            JSON.stringify({ message, data: response?.data }),
          );

          showNotification(
            getErrorNotification({
              message: 'You can not cancel this session',
              closeManually: true,
              withDismissButton: false,
            }),
          );
        }
      }
    },
    [
      getScheduledSlots,
      setSlots,
      currentEvent?.eventId,
      scheduledSlotsTotal,
      scheduledSlots,
    ],
  );

  const handleJoinEvent = useCallback((eventId?: string) => {
    if (eventId) {
      navigate(ROUTES.EVENT_PAGE(eventId));
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    resetState();
    loadSlotsData();
  }, [loadSlotsData, resetState]);

  return (
    <OfficeHoursContext.Provider
      value={{
        upcomingSlots: slots ?? [],
        isSlotsLoading,
        loadNextPageSlots: handleLoadNextPageSlots,

        bookSlot: handleBook,
        cancelSlot: handleCancel,
        joinSlot: handleJoinEvent,

        scheduledSlots: scheduledSlots ?? [],
        isScheduledSlotsLoading,
        loadScheduledSlots: getScheduledSlots,
        loadNextPageScheduledSlots: handleLoadNextPageScheduledSlots,

        loadSlots: loadSlotsData,

        upcomingSlotsTotal,
        scheduledSlotsTotal,
      }}
    >
      {children}
    </OfficeHoursContext.Provider>
  );
};
